import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { BookedTrip, Flight, Seatmap, Passenger } from '@ibe/models';
import { Store } from '@ngrx/store';
import * as fromStore from '../../store';
import * as BookedTripActions from '@ibe/store/actions/booked-trip.actions';
import * as SeatmapActions from '@ibe/store/actions/seatmap.actions';
import { environment } from '../../../environments/environment';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TAPDataStorageService {
	private _bookedTrips: Map<string, BookedTrip>;
	private _activeBooking: BookedTrip;
	private seatMapData: BehaviorSubject<Seatmap> = new BehaviorSubject(null);

	private readonly _observableBookedTrips: BehaviorSubject<BookedTrip[]>;
	private readonly _observableCurrentBooking: BehaviorSubject<BookedTrip>;

	private _activeBookingMockLoaded: BehaviorSubject<boolean>;
	private _myBookingsMocksLoaded: BehaviorSubject<boolean>;
	private _retrievePNRLocatorMocksLoaded: BehaviorSubject<boolean>;

	constructor(
		private store: Store<fromStore.State>,
		protected _http: HttpClient
	) {
		this._bookedTrips = new Map();
		this._activeBookingMockLoaded = new BehaviorSubject(false);
		this._myBookingsMocksLoaded = new BehaviorSubject(false);
		this._retrievePNRLocatorMocksLoaded = new BehaviorSubject(false);

		this._activeBooking = new BookedTrip([], null, 'round trip');

		this._observableBookedTrips = new BehaviorSubject(this.bookedTrips);
		this._observableCurrentBooking = new BehaviorSubject(this.activeBooking);
	}

	initializeSeatmap(planeModel: string) {
		this.readSeatmapData(planeModel).subscribe(seatmap => {
			this.store.dispatch(new SeatmapActions.PutSeatmapAction(seatmap));
		});
	}

	get activeBookingMockLoaded() {
		return this._activeBookingMockLoaded;
	}

	get retrievePNRLocatorMocksLoaded() {
		return this._retrievePNRLocatorMocksLoaded;
	}

	setRetrievePNRLocatorMocksLoaded(next: boolean) {
		this._retrievePNRLocatorMocksLoaded.next(next);
	}

	get myBookingsMocksLoaded() {
		return this._myBookingsMocksLoaded;
	}

	get seatmap() {
		return this.seatMapData;
	}

	get bookedTrips() {
		const trips = [];

		this._bookedTrips.forEach(val => trips.push(val));
		return trips;
	}

	set bookedTrips(trips: BookedTrip[]) {
		this._bookedTrips.clear();
		trips.forEach(trip => this._bookedTrips.set(trip.pnr, trip));
	}

	get activeBooking() {
		return this._activeBooking;
	}

	set activeBooking(booking: BookedTrip) {
		this._activeBooking = booking;
	}

	get observableBookings() {
		return this._observableBookedTrips;
	}
	get observableCurrentBooking() {
		return this._observableCurrentBooking;
	}

	/**
	 * Initializes the data store with mock booked trips loaded from a file
	 */
	initializeWithMockBookedTrips() {
		this.readTripsInitializerFile();
	}

	/**
	 * Initializes the data store with a mock booking loaded from a file
	 */
	async initializeActiveMockBooking() {
		await this.readActiveBookingInitializerFile();
	}

	/**
	 * Initializes the data store with mocked booked trips if there's no
	 * data present in it already
	 */
	initializeBookingsIfEmpty() {
		if (this.bookedTrips.length === 0) {
			this.initializeWithMockBookedTrips();
		}
	}

	/**
	 * Initializes the data store with a mocked booking if there's no
	 * data present in it already
	 */
	initializeActiveBookingIfEmpty() {
		if (this.activeBooking.flights.length === 0) {
			this.initializeActiveMockBooking();
		}
	}

	/**
	 * Fills up mock costs for flight calendar/graph view
	 */
	generateMockCosts() {
		const today = new Date(),
			oneYear = new Date(),
			result = [];

		oneYear.setDate(oneYear.getDate() + 365);
		const forceDaysWithPrice = 61; // first two months - dev only

		/** Generate true with a 80% probability on average */
		const truthGenerator = () => Math.random() > 0.2;

		/** Generates a random number between min and the given max with two decimal places */
		const generateCost = (min = 30, max = 967.52) =>
			Number.parseFloat((Math.random() * max).toFixed(2)) + min;

		const generateAmount = (dayWithFLights, currentDay) => {
			if (dayWithFLights) {
				const val = generateCost();
				const dayWithPrice = (currentDay > forceDaysWithPrice) ? truthGenerator() : true;
				return {
					currency: {
						value: dayWithPrice ? val.toFixed(2) : null,
						unit: dayWithPrice ? environment.currency.symbol : '',
						name: dayWithPrice ? environment.currency.label : ''
					},
					points: dayWithPrice ? Math.round(val * 50 + 50) : null
				};
			} else {
				return null;
			}
		};
		
		let currentDay = 1;
		while (today <= oneYear) {
			const dayWithFLights = truthGenerator();
			result.push({
				date: new Date(today),
				ammount: generateAmount(dayWithFLights, currentDay),
				no_flights: !dayWithFLights ? this.getNoFlightsCause() : false
			});

			today.setDate(today.getDate() + 1);
			currentDay++;
		}
		return result;
	}
	
	getNoFlightsCause() {
		return Math.random() > 0.5 ? 'no flights' : 'sold out';
	}

	generateMockCostsPromise(delay: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const costs = this.generateMockCosts();

			setTimeout(() => {
				resolve(costs);
			}, delay * 1000);
		});
	}

	/**
	 * Resets the state of the current booking to an empty one
	 */
	resetActiveBooking() {
		this.activeBooking = new BookedTrip([], null, 'round trip');
		this.activeBooking.addPassenger('adult');
		this.observableCurrentBooking.next(this.activeBooking);
		this._activeBookingMockLoaded.next(false);
	}

	/**
	 * Retrieves a booked trip from the data store by its pnr identifier.
	 * Returns the booked trip with that pnr if found, or undefined otherwise
	 * @param {string} pnr - the booked trip identifier
	 */
	getBookedTrip(pnr: string): BookedTrip {
		return this._bookedTrips.get(pnr);
	}

	/**
	 * Stores the given booked trip in the storage
	 * @param {BookedTrip} trip - the trip to store
	 */
	addBookedTrip(trip: BookedTrip) {
		this._bookedTrips.set(trip.pnr, trip);
		this._observableBookedTrips.next(this.bookedTrips);
	}

	/**
	 * Update booking trip
	 * @param {string} pnr of booking
	 * @param {BookedTrip} updated_booked_trip Booked trip to update
	 * @returns booking trip
	 */
	updateBooking(pnr: string, updated_booked_trip: BookedTrip) {
		this._bookedTrips.set(pnr, updated_booked_trip);
		this._observableBookedTrips.next(this.bookedTrips);
		this.store.dispatch(new BookedTripActions.PutBookedTripAction(updated_booked_trip));
	}

	/**
	 * Reads JSON file and import mock data for active booking
	 */
	readActiveBookingInitializerFile(): Promise<BookedTrip> {
		const url = './assets/data/active_booking.json';
		const filePromise = this._http.get(url).toPromise();

		return new Promise((resolve, reject) => {

			filePromise.then(
				(booking: BookedTrip) => {
					const originalPassengers: any = booking.passengers;

					booking.flights.forEach(flight => {
						flight.departure.time = new Date(flight.departure.time);
						flight.arrival.time = new Date(flight.arrival.time);
						if (flight.check_in) {
							flight.check_in.time = new Date(flight.check_in.time);
						}

						if (flight.stops) {
							flight.stops = flight.stops.map(stop => {
								stop.time = new Date(stop.time);
								stop.departure = new Date(stop.departure);
								return stop;
							});
						}
					});

					let neu_flight;
					const flights = booking.flights.map(flight => {
						neu_flight = new Flight(
							flight.departure,
							flight.departure,
							flight.number
						);
						Object.assign(neu_flight, flight);
						return neu_flight;
					});

					const neu_booking = new BookedTrip(
						flights,
						booking.pnr,
						booking.booking_type,
						booking.status
					);

					if (originalPassengers.adults && originalPassengers.adults > 0) {
						for (let i = 0; i < originalPassengers.adults; i++) {
							neu_booking.addPassenger('adult');
						}
					}

					if (originalPassengers.children && originalPassengers.children > 0) {
						for (let i = 0; i < originalPassengers.children; i++) {
							neu_booking.addPassenger('child');
						}
					}

					if (originalPassengers.infants && originalPassengers.infants > 0) {
						for (let i = 0; i < originalPassengers.infants; i++) {
							neu_booking.addPassenger('infant');
						}
					}

					this.activeBooking = neu_booking;
					this._activeBookingMockLoaded.next(true);

					resolve(neu_booking);
				},
				err => {
					reject(err);
				}
			);
		});

	}

	/**
	 * Reads JSON files and import mock data for trips and passengers
	 */
	private readTripsInitializerFile() {
		const urlTripsFile = './assets/data/booked_trips.json';
		this.readInitializerFile(urlTripsFile);
	}

	/**
	 * Reads JSON file and import mock data for retrieve PNR locator
	 */
	public initializerRetrievePNRLocator(tripPNR) {
		const urlTripsFile = './assets/data/booked_trips_find.json';
		this.readInitializerFile(urlTripsFile, tripPNR);
	}

	/**
	 * Reads JSON file and import mock data for retrieve PNR locator
	 */
	public initializerRetrievePNRLocatorGroups(tripPNR) {
		const urlTripsFile = './assets/data/booked_trips_find-group.json';
		this.readInitializerFile(urlTripsFile, tripPNR);
	}

	/**
	 * Reads JSON files and import mock data for trips and passengers
	 */
	private readInitializerFile(urlTripsFile, customPNR?) {
		const urlTrips = urlTripsFile;
		const urlPassengers = './assets/data/passengers.json';
		const filePromiseTrips = this._http.get<any[]>(urlTrips).toPromise();
		const filePromisePassengers = this._http
			.get<any[]>(urlPassengers)
			.toPromise();

		forkJoin(filePromiseTrips, filePromisePassengers).subscribe(
			result => {
				const trips = result[0];
				const passengersExampleOri = result[1];

				let countTrip = 0;
				trips.forEach((trip: BookedTrip) => {
					let passengersExample = passengersExampleOri;

					trip.flights.forEach(flight => {
						flight.departure.time = new Date(flight.departure.time);
						flight.arrival.time = new Date(flight.arrival.time);
						flight.check_in.time = new Date(flight.check_in.time);
						if (flight.stops) {
							flight.stops = flight.stops.map(stop => {
								stop.time = new Date(stop.time);
								stop.departure = new Date(stop.departure);
								return stop;
							});
						}
					});

					let neu_flight;
					let flights = trip.flights.map(flight => {
						neu_flight = new Flight(
							flight.departure,
							flight.departure,
							flight.number
						);
						Object.assign(neu_flight, flight);
						return neu_flight;
					});

					if (customPNR) {
						trip.pnr = customPNR;
						trip.added_to_account = false;
					} else {
						trip.added_to_account = true;
					}

					let trip_passengers_info;
					if (trip.passengers_info === undefined) {
						trip_passengers_info = [];
					} else {
						trip_passengers_info = trip.passengers_info.map(cur => Object.assign(new Passenger(), cur));
						flights = flights.map(flight => Object.assign(new Flight(null, null, null), flight, { passengerInfo: [] }));
						flights.forEach(flight => { trip_passengers_info.forEach(passenger => flight.assignPassenger(passenger)); });
					}

					const booking = new BookedTrip(
						flights,
						trip.pnr,
						trip.booking_type,
						trip.status,
						trip.passengers,
						trip_passengers_info,
						trip.payment,
						trip.contact_details,
						trip.is_time_to_think,
						trip.isCorporate,
						trip.time_to_think,
						trip.stopover,
						trip.travel_agency,
						trip.added_to_account,
						trip.loyalty_program,
						trip.is_group
					);
					const originalPassengers: any = trip.passengers;

					if (trip.passengers_info === undefined) {


						/**
						 * To test
						 */
						if (trip.pnr === 'TEST12') {
							passengersExample = passengersExampleOri.filter(cur => ['Test', 'Test', 'Test'].includes(cur.firstName));
						}

						const randomPassengerIDs = [];
						const randomPassenger = new Array(
							originalPassengers.adults +
							originalPassengers.children +
							originalPassengers.infants
						)
							.fill(0)
							.map(function (n) {
								// Prevent repeated Exemple ID's
								let newPassengerID =
									Math.floor(Math.random() * passengersExample.length - 1) + 1;
								while (randomPassengerIDs.includes(newPassengerID)) {
									newPassengerID =
										Math.floor(Math.random() * passengersExample.length - 1) + 1;
								}
								randomPassengerIDs.push(newPassengerID);
								return newPassengerID;
							});

						let passengerIndex = 0;

						if (originalPassengers.adults && originalPassengers.adults > 0) {
							for (let i = 0; i < originalPassengers.adults; i++) {
								booking.addPassenger('adult');
								booking.addPassengerDetails(
									passengersExample[randomPassenger[passengerIndex]],
									passengerIndex
								);
								passengerIndex++;
							}
						}

						if (
							originalPassengers.children &&
							originalPassengers.children > 0
						) {
							for (let i = 0; i < originalPassengers.children; i++) {
								booking.addPassenger('child');
								booking.addPassengerDetails(
									passengersExample[randomPassenger[passengerIndex]],
									passengerIndex
								);
								passengerIndex++;
							}
						}

						if (originalPassengers.infants && originalPassengers.infants > 0) {
							for (let i = 0; i < originalPassengers.infants; i++) {
								booking.addPassenger('infant');
								booking.addPassengerDetails(
									passengersExample[randomPassenger[passengerIndex]],
									passengerIndex
								);
								passengerIndex++;
							}
						}
					}

					this.addBookedTrip(booking);

					++countTrip;
					if (trips.length == countTrip) this._observableBookedTrips.complete();
				});

				if (customPNR) {
					this._retrievePNRLocatorMocksLoaded.next(true);
				} else {
					this._myBookingsMocksLoaded.next(true);
				}
			},
			err => {
				console.error(err);
			}
		);

	}

	/**
	 * Reads JSON file and import mock data for seatmap
	 */
	private readSeatmapData(planeModel: string): Observable<any> {

		let url: any = false;
		// let url = './assets/data/seatmap-wide.json';

		if (planeModel) {
			url = `./assets/data/${planeModel}.json`;
		}

		if (!url) {
			const observable = new BehaviorSubject(false);
			return observable;
		} else {
			return this._http.get(url)
				.pipe(
					tap((seatmap: Seatmap) => seatmap),
					catchError((error: Response) => this.handleError(error))
				);
		}
	}

	/**
	 * Add PNR's trip from "Find existing booking" to active bookings list
	 * @param pnr PNR of trip to add to My Bookings
	 */
	addBookingToAccount(pnr) {
		let tripToChange = this.getBookedTrip(pnr);
		tripToChange = Object.assign(new BookedTrip(null, null, null), tripToChange, { added_to_account: true });
		this.addBookedTrip(tripToChange);
	}

	private handleError(error: Response): any {
		throw new Error('login error: ' + error.statusText);
	}
}
